import React from 'react';
import {
  Users,
  Target,
  BarChart,
  Heart,
  Shield,
  Globe,
  Mail,
  MapPin,
  Phone,
  LinkedinIcon,
  TwitterIcon,
  GithubIcon
} from 'lucide-react';

const About = () => {
    const values = [
        {
          icon: <Heart className="w-8 h-8 text-red-500" />,
          title: "User-Centric",
          description: "Our users are at the core of everything we do. We are dedicated to crafting intuitive, user-friendly solutions that address real-world challenges, ensuring a seamless experience for employers and candidates alike."
        },
        {
          icon: <Shield className="w-8 h-8 text-blue-500" />,
          title: "Trust & Security",
          description: "We prioritize safeguarding sensitive data with robust security measures and transparent practices. Trust is the foundation of our platform, and we strive to provide a safe and reliable environment for all users."
        },
        {
          icon: <Target className="w-8 h-8 text-green-500" />,
          title: "Innovation",
          description: "We continuously seek to push the boundaries of recruitment technology. By embracing creativity and cutting-edge advancements, we transform traditional hiring processes into modern, efficient, and intelligent systems."
        },
        {
          icon: <Globe className="w-8 h-8 text-purple-500" />,
          title: "Global Impact",
          description: "We believe in making a meaningful difference in recruitment practices worldwide. By fostering diversity, inclusion, and equal opportunities, we empower businesses and individuals to thrive on a global scale."
        }
      ];
      
      

  const stats = [
    { value: "15", label: "Hours Saved Per Hire" },
    { value: "150+", label: "Countries" },
    { value: "3X", label: "Expected Productivity Boost for Recruiters" },
    { value: "99%", label: "Accuracy in Candidate-Role Matching" }
  ];

  const teamMembers = [
    {
      name: "Sarah Johnson",
      role: "CEO & Co-founder",
      image: "/api/placeholder/150/150",
      social: {
        linkedin: "#",
        twitter: "#"
      }
    },
    {
      name: "Michael Chen",
      role: "CTO & Co-founder",
      image: "/api/placeholder/150/150",
      social: {
        linkedin: "#",
        github: "#"
      }
    },
    {
      name: "Emma Williams",
      role: "Head of Product",
      image: "/api/placeholder/150/150",
      social: {
        linkedin: "#",
        twitter: "#"
      }
    },
    {
      name: "David Miller",
      role: "Head of AI",
      image: "/api/placeholder/150/150",
      social: {
        linkedin: "#",
        github: "#"
      }
    }
  ];

  return (
    <div className="pt-16">
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 opacity-90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center text-white">
            <h1 className="text-4xl font-extrabold sm:text-5xl lg:text-6xl">
              Revolutionizing Recruitment
            </h1>
            <p className="mt-6 text-xl max-w-3xl mx-auto">
              We're on a mission to transform how companies hire talent, making the process faster, fairer, and more efficient through AI-driven innovation.
            </p>
          </div>
        </div>
      </div>
      </div>

      {/* Stats Section */}
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600">{stat.value}</div>
                <div className="mt-2 text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Story Section */}
        <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div>
                <h2 className="text-3xl font-bold text-gray-900">Our Story</h2>
                <p className="mt-4 text-lg text-gray-600">
                In 2020, Prodigium Recruit was born out of a shared vision: to address the inefficiencies and frustrations in modern recruitment. Our founders, with decades of combined experience in technology and human resources, witnessed firsthand the challenges faced by businesses and job seekers alike. The recruitment process was often plagued by inefficiencies, biases, and outdated methods, leaving organizations struggling to find the right talent and candidates feeling overlooked.
                </p>
                <p className="mt-4 text-lg text-gray-600">
                Recognizing the potential of artificial intelligence to transform this landscape, we embarked on a journey to create a smarter, more equitable way to connect talent with opportunity. Driven by our mission to empower both employers and candidates, we expanded our solution into a full-scale recruitment platform. At the heart of our growth is a commitment to innovation and user-centric design. We believe that technology should enhance human potential, not replace it. Our journey is just beginning. As we continue to evolve, we remain focused on breaking barriers in recruitment through cutting-edge technology and meaningful partnerships. Our vision is to create a world where hiring is faster, smarter, and more human-centric, ensuring that every candidate finds their rightful place and every business unlocks the potential of their workforce.
                </p>
            </div>
            <div className="mt-8 lg:mt-0">
                <img 
                src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600" 
                alt="Company Story" 
                className="rounded-xl shadow-lg"
                />
            </div>
            </div>
        </div>
        </div>


      {/* Values Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <div className="mb-4">{value.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* Contact Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-600 rounded-2xl p-8 lg:p-12">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-8">Get in Touch</h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="flex items-center justify-center text-blue-100">
                  <Mail className="w-6 h-6 mr-2" />
                  <span>contactus@prodigiumrecruit.ai</span>
                </div>
                <div className="flex items-center justify-center text-blue-100">
                  <Phone className="w-6 h-6 mr-2" />
                  <span>+1 (530) 302-5546</span>
                </div>
                <div className="flex items-center justify-center text-blue-100">
                  <MapPin className="w-6 h-6 mr-2" />
                  <span>New Jersey</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;